import { useRef, useState } from 'react';
import type { AdminConversation, AdminMessage } from '../../api/types.js';
import { formatCny } from '../../lib/kimi-k26-cost.js';
import {
  botAccountDisplayName,
  displayName,
  formatRelative,
  modeText,
  QUICK_REPLY_TEMPLATES,
  readableWechatId,
} from '../../lib/utils.js';
import { MessageList } from './MessageList.js';
import { FeedbackPopover } from '../feedback/FeedbackPopover.js';
import { Avatar } from '../ui/Avatar.js';
import { BotAccountBadge } from '../ui/BotAccountBadge.js';
import type { MessageFeedbackInput } from '../../api/types.js';
import type { MessageFeedback } from '../../api/types.js';

interface ChatPanelProps {
  conversation: AdminConversation | null;
  messages: AdminMessage[];
  messageFeedback: Record<number, MessageFeedback>;
  quality: { score: number; summary: string; issues: { dimension: string; detail: string }[] } | null;
  loading: boolean;
  sending: boolean;
  conversationAiCost?: { totalCny: number; turns: number };
  hasMoreMessages?: boolean;
  onLoadOlderMessages?: (friendId: number) => void;
  onModeToggle: (conversation: AdminConversation) => void;
  onQualityCheck: (friendId: number) => void;
  onSendReply: (conversation: AdminConversation, text: string) => void;
  onSaveMessageFeedback: (messageId: number, input: MessageFeedbackInput) => Promise<void>;
  onRefreshMessages: (conversation: AdminConversation) => void;
  onShowList: () => void;
  onOpenProfile: () => void;
  onOpenSettings: () => void;
}

export function ChatPanel({
  conversation,
  messages,
  messageFeedback,
  quality,
  loading,
  sending,
  conversationAiCost,
  hasMoreMessages,
  onLoadOlderMessages,
  onModeToggle,
  onQualityCheck,
  onSendReply,
  onSaveMessageFeedback,
  onRefreshMessages,
  onShowList,
  onOpenProfile,
  onOpenSettings,
}: ChatPanelProps) {
  const [input, setInput] = useState('');
  const [checking, setChecking] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const name = conversation ? displayName(conversation) : '选择会话';
  const customerWechatAlias = readableWechatId(conversation?.customerWechatAlias);

  const handleSend = () => {
    if (!conversation || !input.trim() || conversation.mode !== 'manual') return;
    onSendReply(conversation, input);
    setInput('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleQuickReply = (text: string) => {
    if (!conversation || conversation.mode !== 'manual') return;
    onSendReply(conversation, text);
  };

  const handleQualityClick = async () => {
    if (!conversation) return;
    setChecking(true);
    try {
      await onQualityCheck(conversation.friendId);
    } finally {
      setChecking(false);
    }
  };

  const renderFeedbackButton = (message: AdminMessage) => {
    const canFeedback = Number.isFinite(Number(message.id));
    if (!canFeedback) return null;
    return (
      <FeedbackPopover
        message={message}
        feedback={messageFeedback[Number(message.id)] || null}
        onSave={onSaveMessageFeedback}
      />
    );
  };

  return (
    <>
      <header className="chat-toolbar">
        <div className="active-customer">
          <button className="button ghost mobile-menu-btn" type="button" onClick={onShowList}>
            ‹ 会话
          </button>
          <Avatar name={name} size="md" />
          <div className="toolbar-title">
            <div className="toolbar-heading">
              <strong>{name}</strong>
              {conversation ? (
                <BotAccountBadge
                  botWechatRecordId={conversation.botWechatRecordId}
                  botNickname={conversation.botNickname}
                  prefix="接待账号："
                />
              ) : null}
            </div>
            <span>
              {conversation
                ? `${customerWechatAlias ? `微信号 ${customerWechatAlias} · ` : ''}friend_id ${conversation.friendId} · 上次活跃 ${formatRelative(conversation.lastMessageAt)}${
                  conversationAiCost && conversationAiCost.turns > 0
                    ? ` · AI 合计 ${formatCny(conversationAiCost.totalCny)} · ${conversationAiCost.turns} 轮`
                    : ''
                }`
                : '聊天记录反馈工具 · 选择会话后查看记录并标注反馈'}
            </span>
          </div>
        </div>
        <div className="toolbar-actions">
          <button
            className="button mobile-only"
            type="button"
            disabled={!conversation}
            onClick={onOpenProfile}
          >
            详情
          </button>
          <button className="button ghost" type="button" onClick={onOpenSettings}>
            工作微信设置
          </button>
          <button
            className="button ghost"
            type="button"
            disabled={!conversation || loading}
            onClick={() => conversation && onRefreshMessages(conversation)}
          >
            {loading && messages.length > 0 ? '刷新中' : '刷新记录'}
          </button>
          {conversation?.isWhitelisted ? <span className="tag whitelist">白名单</span> : null}
          {conversation ? (
            <span className={`tag ${conversation.mode === 'manual' ? 'manual' : ''}`}>{modeText(conversation.mode)}</span>
          ) : null}
          <button
            className="button"
            type="button"
            disabled={!conversation}
            onClick={() => conversation && onModeToggle(conversation)}
          >
            {conversation?.mode === 'manual' ? '切回自动' : '接管'}
          </button>
          <button
            className="button primary"
            type="button"
            disabled={!conversation || checking}
            onClick={handleQualityClick}
          >
            {checking ? '质检中' : 'AI 质检'}
          </button>
        </div>
      </header>

      {conversation ? (
        loading && messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-illustration">...</div>
            <strong>加载消息中</strong>
          </div>
        ) : (
          <>
            {hasMoreMessages && conversation && onLoadOlderMessages ? (
              <div className="load-older-messages">
                <button
                  className="button ghost"
                  type="button"
                  onClick={() => onLoadOlderMessages(conversation.friendId)}
                >
                  更早的记录
                </button>
              </div>
            ) : null}
            <MessageList
              messages={messages}
              conversationName={name}
              botName={botAccountDisplayName(conversation)}
              renderFeedbackButton={renderFeedbackButton}
            />
          </>
        )
      ) : (
        <div className="empty-state">
          <div className="empty-illustration">暖</div>
          <strong>从左侧选择一个客户开始聊天</strong>
        </div>
      )}

      <footer className="composer">
        {conversation && conversation.mode === 'manual' ? (
          <div className="quick-replies">
            {QUICK_REPLY_TEMPLATES.map((text, index) => (
              <button
                key={index}
                className="quick-reply"
                type="button"
                onClick={() => handleQuickReply(text)}
                title={text}
              >
                {index + 1}. {text}
              </button>
            ))}
          </div>
        ) : null}
        <div className="composer-row">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="人工模式下输入消息，Enter 发送，Shift+Enter 换行"
            disabled={!conversation || conversation.mode !== 'manual' || sending}
          />
          <button
            className="button primary send-button"
            type="button"
            disabled={!conversation || conversation.mode !== 'manual' || sending || !input.trim()}
            onClick={handleSend}
          >
            {sending ? <span className="spinner" /> : '发送'}
          </button>
        </div>
        {quality ? (
          <div className="quality-banner">
            质检 <span className="quality-score">{quality.score}</span> 分 · {quality.summary}
            {quality.issues && quality.issues.length > 0 ? (
              <span className="quality-issues">
                {' '}
                · {quality.issues.map((issue) => `${issue.dimension}: ${issue.detail}`).join('；')}
              </span>
            ) : null}
          </div>
        ) : null}
      </footer>
    </>
  );
}
